// Project Overview model — pure functions over the segment payloads of
// GET /api/overview/<id>/segment. The PDF builds the same numbers server-side:
// this file is the twin of Backend/123bimlive/utils/overview_data.py
// (filter_sites, cluster_sites, union_miles, summarize, number_segments).
// Change one, change the other.

export const ANALYSES = ['SSD', 'PSD', 'HCA', 'GLR', 'FSA', 'GRW', 'ISD'];
export const SEVERITIES = ['severe', 'moderate', 'minor'];
export const SEVERITY_RANK = { severe: 3, moderate: 2, minor: 1 };
export const POINT_PAD_MI = 0.02;   // an intersection has no length: it counts this far to each side

export const ANALYSIS_COLORS = {
  SSD: '#c92a2a', PSD: '#9c36b5', GLR: '#e8a30c', FSA: '#1971c2', HCA: '#e8590c', GRW: '#5f3dc4', ISD: '#0ca678',
};
export const SEVERITY_COLORS = { severe: '#e03131', moderate: '#f08c00', minor: '#fcc419' };
export const ANALYSIS_NAMES = {
  SSD: 'Stopping sight distance', PSD: 'Passing sight distance', HCA: 'Horizontal curves', GLR: 'Sun glare',
  FSA: 'Flat spots (drainage)', GRW: 'Guardrail warrant', ISD: 'Intersection sight distance',
};
export const ROAD_COLORS = ['#7aa7e8', '#9bd1c0', '#d6b3f0', '#f0c9a0', '#c8d18f', '#e8a3b5', '#8fd0e8', '#b9b3f0'];

export const segmentName = (seg) => (seg.segment ? `${seg.road} · ${seg.segment}` : seg.road);

/** Number the corridors of a multi-corridor road (by milepost) when the label carried none. */
export function numberSegments(segments) {
  const out = segments.map((s) => ({ ...s }));
  const byRoad = new Map();
  out.forEach((s) => { if (!byRoad.has(s.road)) byRoad.set(s.road, []); byRoad.get(s.road).push(s); });
  byRoad.forEach((group) => {
    if (group.length < 2) return;
    group.sort((a, b) => ((a.segment == null) - (b.segment == null)) || ((a.segment || 0) - (b.segment || 0))
      || ((a.mpMin || 0) - (b.mpMin || 0)));
    const used = new Set(group.map((s) => s.segment).filter(Boolean));
    let n = 0;
    group.forEach((s) => {
      if (s.segment) return;
      n += 1;
      while (used.has(n)) n += 1;
      s.segment = n;
      used.add(n);
    });
  });
  return out;
}

/** Flat site rows, each carrying its segment's identity, that pass the filters. */
export function filterSites(segments, filters = {}) {
  const analyses = new Set(filters.analyses && filters.analyses.length ? filters.analyses : ANALYSES);
  const keys = new Set(filters.segments ? filters.segments : segments.map((s) => s.key));
  const severities = new Set(filters.severities && filters.severities.length ? filters.severities : SEVERITIES);
  const direction = filters.direction || '';
  const rows = [];
  segments.forEach((seg) => {
    if (!keys.has(seg.key)) return;
    (seg.sites || []).forEach((s) => {
      if (!analyses.has(s.analysis) || !severities.has(s.severity)) return;
      if (direction && s.direction !== direction) return;
      rows.push({ ...s, segKey: seg.key, road: seg.road, segment: seg.segment, projectId: seg.projectId });
    });
  });
  return rows;
}

/** Deficient length without double counting: intervals merged per corridor and direction. */
export function unionMiles(rows) {
  const spans = new Map();
  rows.forEach((r) => {
    if (r.mpStart == null || !(r.lengthMi > 0)) return;
    const k = `${r.segKey}|${r.direction}`;
    if (!spans.has(k)) spans.set(k, []);
    spans.get(k).push([r.mpStart, r.mpEnd]);
  });
  let total = 0;
  spans.forEach((items) => {
    items.sort((a, b) => a[0] - b[0]);
    let [lo, hi] = items[0];
    items.slice(1).forEach(([a, b]) => {
      if (a <= hi) hi = Math.max(hi, b);
      else { total += hi - lo; lo = a; hi = b; }
    });
    total += hi - lo;
  });
  return total;
}

/** [lat, lng] at milepost `mp`: along the geometry of the item that covers it (or is nearest). */
export function locate(items, mp) {
  let best = null;
  items.forEach((r) => {
    if (r.mpStart == null) return;
    const b = r.mpEnd != null ? r.mpEnd : r.mpStart;
    const gap = r.mpStart <= mp && mp <= b ? 0 : Math.min(Math.abs(mp - r.mpStart), Math.abs(mp - b));
    if (!best || gap < best.gap) best = { gap, r, a: r.mpStart, b };
  });
  if (!best) return [null, null];
  const coords = (best.r.coords || []).filter((c) => c && c[0] != null);
  if (coords.length < 2 || best.b - best.a < 1e-9) return [best.r.lat, best.r.lng];
  const t = Math.min(1, Math.max(0, (mp - best.a) / (best.b - best.a)));
  return coords[Math.round(t * (coords.length - 1))];
}

/**
 * Stretches of one corridor where two or more DIFFERENT analyses are deficient at
 * the same place: their milepost intervals truly OVERLAP. Runs count exactly as
 * surveyed; only a point feature (an intersection) gets `pointPad` to each side.
 * A cluster spans exactly the overlap. Direction is ignored.
 */
export function clusterSites(rows, pointPad = POINT_PAD_MI) {
  const bySeg = new Map();
  rows.forEach((r) => {
    if (r.mpStart == null) return;
    if (!bySeg.has(r.segKey)) bySeg.set(r.segKey, []);
    bySeg.get(r.segKey).push(r);
  });
  const raw = [];
  bySeg.forEach((segRows) => {
    const spans = segRows.map((r) => {
      const end = r.mpEnd != null ? r.mpEnd : r.mpStart;
      const pad = end - r.mpStart < 1e-9 ? pointPad : 0;
      return [r.mpStart - pad, end + pad, r];
    });
    const cuts = Array.from(new Set(spans.flatMap(([a, b]) => [a, b]))).sort((a, b) => a - b);
    let cur = null;
    for (let i = 0; i + 1 < cuts.length; i += 1) {
      const lo = cuts[i];
      const hi = cuts[i + 1];
      const mid = (lo + hi) / 2;
      if (hi - lo < 1e-9) continue;   // eslint-disable-line no-continue
      const live = spans.filter(([a, b]) => a <= mid && mid <= b).map((s) => s[2]);
      if (new Set(live.map((r) => r.analysis)).size >= 2) {
        if (!cur) { cur = { lo, hi, items: new Map() }; raw.push(cur); }
        cur.hi = hi;
        for (const r of live) cur.items.set(r.id, r);
      } else {
        cur = null;
      }
    }
  });
  return raw.map((c) => {
    const items = Array.from(c.items.values()).sort((a, b) => a.mpStart - b.mpStart);
    const mpStart = c.lo;
    const mpEnd = c.hi;
    const analyses = ANALYSES.filter((a) => items.some((i) => i.analysis === a));
    const worst = items.reduce((m, i) => ((SEVERITY_RANK[i.severity] || 0) > (SEVERITY_RANK[m.severity] || 0) ? i : m), items[0]);
    const reports = {};
    analyses.forEach((a) => {
      const hit = items.find((i) => i.analysis === a && i.reportUrl);
      if (hit) reports[a] = hit;
    });
    const first = items[0];
    return {
      id: `${first.segKey}|${mpStart.toFixed(4)}`,
      segKey: first.segKey, road: first.road, segment: first.segment, projectId: first.projectId,
      mpStart, mpEnd, spanMi: Math.round((mpEnd - mpStart) * 10000) / 10000,
      analyses, n: analyses.length, sites: items.length, severity: worst.severity,
      lat: locate(items, (mpStart + mpEnd) / 2)[0], lng: locate(items, (mpStart + mpEnd) / 2)[1], reports, items,
    };
  });
}

const asList = (x) => (Array.isArray(x) ? x : [x]);

/** [mpMin, mpMax] of one surveyed direction of a corridor, or null. */
function dirInterval(seg, d) {
  const info = (seg.directions || {})[d] || {};
  if (info.mpMin != null && info.mpMax != null) return [info.mpMin, info.mpMax];
  if (info.lengthMi) return [seg.mpMin || 0, (seg.mpMin || 0) + info.lengthMi];
  return null;
}

function unionLen(intervals) {
  let total = 0;
  let cur = null;
  [...intervals].sort((a, b) => a[0] - b[0]).forEach(([a, b]) => {
    if (!cur || a > cur[1]) { if (cur) total += cur[1] - cur[0]; cur = [a, b]; } else cur[1] = Math.max(cur[1], b);
  });
  return total + (cur ? cur[1] - cur[0] : 0);
}

/**
 * Surveyed ROAD miles: the UNION of the corridors' milepost ranges per road, for the
 * directions shown. A road surveyed both ways is one road; two corridors (or two projects)
 * covering the same mileposts count that stretch once.
 */
export function roadMiles(segments, direction = '') {
  const dirs = direction ? [direction] : ['P', 'N'];
  const byRoad = new Map();
  let fallback = 0;
  asList(segments).forEach((s) => {
    if (!s.directions || !Object.keys(s.directions).length) { fallback += s.lengthMi || 0; return; }
    dirs.forEach((d) => {
      const iv = dirInterval(s, d);
      if (iv) { if (!byRoad.has(s.road)) byRoad.set(s.road, []); byRoad.get(s.road).push(iv); }
    });
  });
  let total = fallback;
  byRoad.forEach((ivs) => { total += unionLen(ivs); });
  return total;
}

/** Miles of analysed travel direction (union per road and direction): the deficiency percentages' denominator. */
export function directionalMiles(segments, direction = '') {
  const dirs = direction ? [direction] : ['P', 'N'];
  let total = 0;
  dirs.forEach((d, i) => {
    const byRoad = new Map();
    asList(segments).forEach((s) => {
      if (!s.directions || !Object.keys(s.directions).length) {
        if (i === 0) total += direction ? (s.lengthMi || 0) : (s.surveyedMi || s.lengthMi || 0);
        return;
      }
      const iv = dirInterval(s, d);
      if (iv) { if (!byRoad.has(s.road)) byRoad.set(s.road, []); byRoad.get(s.road).push(iv); }
    });
    byRoad.forEach((ivs) => { total += unionLen(ivs); });
  });
  return total;
}

/**
 * Drop a corridor that lies (95%+) inside another corridor of the SAME road and project:
 * a re-upload of the same route under another route token, or a short manual lane inside
 * the full survey. The newer run wins (an unstamped legacy run loses), then the longer one.
 */
export function dedupeSegments(segments) {
  const dropped = new Set();
  const rank = (s) => [s.runStamp || '', (s.mpMax - s.mpMin) || 0, s.key];
  const less = (x, y) => { for (let i = 0; i < x.length; i += 1) { if (x[i] < y[i]) return true; if (x[i] > y[i]) return false; } return false; };
  segments.forEach((a) => segments.forEach((b) => {
    if (a === b || dropped.has(a.key) || dropped.has(b.key)) return;
    if (a.road !== b.road || a.projectId !== b.projectId || a.mpMin == null || b.mpMin == null) return;
    const span = (a.mpMax - a.mpMin) || 0;
    const inside = Math.min(a.mpMax, b.mpMax) - Math.max(a.mpMin, b.mpMin);
    if (span <= 0 || inside < 0.95 * span) return;
    if (less(rank(a), rank(b))) dropped.add(a.key);
  }));
  return segments.filter((s) => !dropped.has(s.key));
}

/** Corridors of one road whose milepost ranges still overlap (usually two projects on one stretch). */
export function roadOverlaps(segments, minMi = 0.1) {
  const out = [];
  const segs = segments.filter((s) => s.mpMin != null);
  segs.forEach((a, i) => segs.slice(i + 1).forEach((b) => {
    if (a.road !== b.road) return;
    const mi = Math.min(a.mpMax, b.mpMax) - Math.max(a.mpMin, b.mpMin);
    if (mi >= minMi) out.push({ road: a.road, a, b, miles: mi });
  }));
  return out;
}

/** Summary rows: one per road or per corridor, site count + deficient miles per analysis. */
export function summarize(segments, rows, by = 'segment', analyses = ANALYSES, direction = '') {
  const groups = new Map();
  segments.forEach((seg) => {
    const k = by === 'road' ? seg.road : seg.key;
    if (!groups.has(k)) {
      groups.set(k, {
        key: k, label: by === 'road' ? seg.road : segmentName(seg), road: seg.road,
        segment: by === 'road' ? null : seg.segment, miles: 0, directional: 0, ranges: [], keys: new Set(), segs: [], firstSeg: seg,
      });
    }
    const g = groups.get(k);
    g.keys.add(seg.key);
    g.segs.push(seg);
    if (seg.mpMin != null) g.ranges.push([seg.mpMin, seg.mpMax]);
  });
  return Array.from(groups.values()).map((g) => {
    g.miles = roadMiles(g.segs, direction);
    g.directional = directionalMiles(g.segs, direction);
    const mine = rows.filter((r) => g.keys.has(r.segKey));
    const deficientMi = unionMiles(mine);
    const byAnalysis = {};
    analyses.forEach((a) => {
      const sub = mine.filter((r) => r.analysis === a);
      byAnalysis[a] = { sites: sub.length, miles: unionMiles(sub) };
    });
    return {
      key: g.key, label: g.label, road: g.road, segment: g.segment, miles: g.miles,
      ranges: g.ranges.sort((a, b) => a[0] - b[0]), sites: mine.length,
      severe: mine.filter((r) => r.severity === 'severe').length,
      directionalMi: g.directional, deficientMi, pct: g.directional ? (deficientMi / g.directional) * 100 : 0, byAnalysis,
      firstSite: mine[0] || null, firstSeg: g.firstSeg,
    };
  });
}

/** milepost, or mileage from the segment start (cumulative along the road when unified). */
export function positionFn(segments, axis, unify) {
  const byKey = new Map(segments.map((s) => [s.key, s]));
  const base = new Map();
  if (unify) {
    const roads = new Map();
    segments.forEach((s) => { if (!roads.has(s.road)) roads.set(s.road, []); roads.get(s.road).push(s); });
    roads.forEach((group) => {
      let run = 0;
      [...group].sort((a, b) => ((a.segment || 0) - (b.segment || 0)) || ((a.mpMin || 0) - (b.mpMin || 0)))
        .forEach((s) => { base.set(s.key, run); run += s.lengthMi || 0; });
    });
  }
  return (segKey, mp) => {
    if (mp == null) return null;
    if (axis !== 'mi') return mp;
    const seg = byKey.get(segKey) || {};
    return (base.get(segKey) || 0) + (mp - (seg.mpMin || 0));
  };
}

/** A segment within a milepost range: what the figures count when the page is limited to it (intersections too). */
export function clipSegment(seg, [lo, hi]) {
  return {
    ...seg,
    mpMin: Math.max(seg.mpMin ?? lo, lo), mpMax: Math.min(seg.mpMax ?? hi, hi),
    lengthMi: Math.max(0, Math.min(seg.mpMax ?? hi, hi) - Math.max(seg.mpMin ?? lo, lo)),
    directions: Object.fromEntries(Object.entries(seg.directions || {}).map(([d, info]) => {
      const a = Math.max(info.mpMin ?? seg.mpMin ?? lo, lo);
      const b = Math.min(info.mpMax ?? seg.mpMax ?? hi, hi);
      return [d, { ...info, mpMin: a, mpMax: Math.max(a, b), lengthMi: Math.max(0, b - a) }];
    })),
    intersections: (seg.intersections || []).filter((ix) => ix.mp != null && ix.mp >= lo && ix.mp <= hi),
  };
}

export function overviewStats(segments, rows, clusters, direction = '') {
  const miles = roadMiles(segments, direction);
  const directional = directionalMiles(segments, direction);
  const crossings = segments.flatMap((s) => s.intersections || []);
  const deficientMi = unionMiles(rows);
  const roads = Array.from(new Set(segments.map((s) => s.road)));
  const perRoad = roads.map((road) => {
    const segs = segments.filter((s) => s.road === road);
    const mi = roadMiles(segs, direction);
    const keys = new Set(segs.map((s) => s.key));
    return { road, rate: mi ? rows.filter((r) => keys.has(r.segKey)).length / mi : 0 };
  }).sort((a, b) => b.rate - a.rate);
  return {
    miles, directionalMi: directional, roads: roads.length, segments: segments.length,
    intersections: crossings.length, intersectionsShort: crossings.filter((i) => i.severity).length,
    maneuvers: crossings.reduce((n, i) => n + (i.maneuvers || []).length, 0),
    maneuversShort: crossings.reduce((n, i) => n + (i.maneuvers || []).filter((m) => typeof m.quality === 'number' && m.quality < 1).length, 0),
    projects: new Set(segments.map((s) => s.projectId)).size,
    sites: rows.length, severe: rows.filter((r) => r.severity === 'severe').length,
    moderate: rows.filter((r) => r.severity === 'moderate').length, minor: rows.filter((r) => r.severity === 'minor').length,
    deficientMi, pct: directional ? (deficientMi / directional) * 100 : 0,
    clusters: clusters.length, clusters3: clusters.filter((c) => c.n >= 3).length,
    densest: perRoad[0] || null,
    // ISD is a point analysis (intersections, maneuvers): it has its own figures above, not sites and miles
    byAnalysis: ANALYSES.filter((a) => a !== 'ISD').map((a) => {
      const sub = rows.filter((r) => r.analysis === a);
      return { analysis: a, sites: sub.length, miles: unionMiles(sub) };
    }),
  };
}

/** Stable sort by a keyed accessor; rows whose value is null sink to the end. */
export function sortRows(rows, sort, accessors) {
  const get = accessors[sort?.key];
  if (!get) return rows;
  const dir = sort.dir === 'desc' ? -1 : 1;
  const cmp = (x, y) => {
    if (Array.isArray(x) && Array.isArray(y)) {
      for (let i = 0; i < x.length; i += 1) { const c = cmp(x[i], y[i]); if (c) return c; }
      return 0;
    }
    return x > y ? 1 : x < y ? -1 : 0;
  };
  return rows.map((r, i) => [r, i]).sort((a, b) => {
    const x = get(a[0]);
    const y = get(b[0]);
    if (x == null && y == null) return a[1] - b[1];
    if (x == null) return 1;
    if (y == null) return -1;
    return cmp(x, y) * dir || a[1] - b[1];
  }).map((p) => p[0]);
}

/**
 * Strip blocks: one per corridor, or one per road when unified. A unified road
 * keeps the milepost axis while its corridors' milepost ranges do not overlap;
 * otherwise it lays them end to end on a mileage axis.
 */
export function stripBlocks(segments, unify) {
  if (!unify) {
    return segments.map((s) => ({
      key: s.key, title: segmentName(s), road: s.road, axisKind: 'mp',
      parts: [{ seg: s, offset: 0, lo: s.mpMin || 0, hi: s.mpMax || 0 }], lo: s.mpMin || 0, hi: s.mpMax || 0,
    }));
  }
  const roads = new Map();
  segments.forEach((s) => { if (!roads.has(s.road)) roads.set(s.road, []); roads.get(s.road).push(s); });
  return Array.from(roads.entries()).map(([road, group]) => {
    const sorted = [...group].sort((a, b) => ((a.segment || 0) - (b.segment || 0)) || ((a.mpMin || 0) - (b.mpMin || 0)));
    const overlap = sorted.some((s, i) => i > 0 && (s.mpMin || 0) < (sorted[i - 1].mpMax || 0) - 1e-6);
    if (!overlap) {
      return {
        key: road, title: road, road, axisKind: 'mp',
        parts: sorted.map((s) => ({ seg: s, offset: 0, lo: s.mpMin || 0, hi: s.mpMax || 0 })),
        lo: sorted[0].mpMin || 0, hi: sorted[sorted.length - 1].mpMax || 0,
      };
    }
    let run = 0;
    const parts = sorted.map((s) => {
      const len = (s.mpMax || 0) - (s.mpMin || 0);
      const part = { seg: s, offset: run - (s.mpMin || 0), lo: run, hi: run + len };
      run += len;
      return part;
    });
    return { key: road, title: road, road, axisKind: 'mi', parts, lo: 0, hi: run };
  });
}
